/**
 * Copyright 2021 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2024 (c) Daniel Littfinski, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import React, { Component } from 'react';
import SoftwareListElement from '../components/softwareList/softwareListElement';
import { connect } from 'react-redux';
import { getFair, getSoftwareListFilter, getSoftwareByFairArray } from '../redux/selectors';
import FilterModal, { IFilterDescription, IFilterModalChild } from '../components/filterModal/filterModal';
import { setSoftwareListFilter, setSoftwareListFilterType } from '../redux/actions';
import FairMap from '../components/fairMap/fairMap';
import { labels } from '@umati/dashboard-common';
import { ISoftware, IFair } from '@umati/dashboard-types';
import { IFilterSoftwareData, SoftwareFilter } from 'utilities/SoftwareFilter';
import { initialSoftwareFilterState } from 'redux/reducers/softwareFilter';

interface ISoftwareListPropsEx {
  softwares: ISoftware[];
  fair?: IFair;
  filter: IFilterSoftwareData;
}

interface ISoftwareListActions {
  setSoftwareListFilter: setSoftwareListFilterType;
}

interface ISoftwareListPropsAll extends ISoftwareListPropsEx, ISoftwareListActions {}

interface ISoftwareListState {
  softwares: ISoftware[];
  expanded: Set<string>;
  showFilterWindow: boolean;
  filter: IFilterSoftwareData;
}

class SoftwareList extends Component<ISoftwareListPropsAll, ISoftwareListState> {
  constructor(props) {
    super(props);

    const softwares = this.props.softwares;
    const expanded = new Set<string>();

    this.handleExpand.bind(this);
    this.filterCallback.bind(this);
    this.mapCallback.bind(this);

    this.state = {
      softwares,
      expanded,
      showFilterWindow: false,
      filter: this.props.filter,
    };
  }

  componentDidMount() {
    const { hash } = window.location;
    if (hash !== '') {
      let retries = 0;
      const id = hash.replace('#', '');
      const scroll = () => {
        const element = document.getElementById(id);
        if (retries >= 20) {
          return;
        } else if (element) {
          setTimeout(() => element.scrollIntoView(), 0);
        } else {
          setTimeout(scroll, 2000);
        }
        retries = retries + 1;
      };
      scroll();
    }
  }

  static getDerivedStateFromProps = (newProps, prevState) => {
    const newState = { ...prevState, softwares: newProps.softwares, filter: newProps.filter };
    const dontUpdateExpanded =
      prevState.softwares !== undefined &&
      newProps.softwares !== undefined &&
      prevState.softwares.length === newProps.softwares.length &&
      prevState.softwares.reduce((acc, val) => acc && newProps.softwares.includes(val), true);
    return SoftwareList.filter(newState.filter, newState, newProps.fair, dontUpdateExpanded);
  };

  isExpanded = (software: ISoftware) => {
    return this.state.expanded.has(software.SoftwareId);
  };

  handleCloseFilterModal = () => {
    this.setState({ showFilterWindow: false });
  };

  static expand = (key, expSet) => {
    if (expSet.has(key)) {
      expSet.delete(key);
    } else {
      expSet.add(key);
    }
    return expSet;
  };

  handleExpand = (key) => {
    const expSet = SoftwareList.expand(key, this.state.expanded);
    this.setState({ ...this.state, expanded: expSet });
  };

  mapCallback = (data) => {
    this.setState({ ...this.state, filter: { ...this.state.filter, halls: data } });
  };

  static filter = (filterData, state, fair, ignoreDetails = false) => {
    state.filter = filterData;

    if (!ignoreDetails) {
      if (filterData.showDetails) {
        state.expanded = new Set(state.softwares.map((s) => s.SoftwareId));
      } else {
        state.expanded = new Set();
      }
    }

    state.filter.fairTag = fair?.Tag;
    return state;
  };

  static isFavorite = (el, favs) => {
    return favs.includes(el.key);
  };

  filterCallback = (filterData) => {
    const state = SoftwareList.filter(filterData, this.state, this.props.fair);
    this.props.setSoftwareListFilter(state.filter);
    this.setState(state);
  };

  render() {
    const defaults = initialSoftwareFilterState;
    const filter: IFilterDescription = {
      checkboxes: [
        {
          cbKey: 'showDetails',
          label: 'Details',
          value: this.state.filter.showDetails,
          default: defaults.showDetails,
          toggleButton: true,
        },
        {
          cbKey: 'showFavourites',
          label: labels.favorites,
          value: this.state.filter.showFavourites,
          default: defaults.showFavourites,
          toggleButton: false,
        },
      ],
      searchbars: [
        {
          cbKey: 'searchValue',
          label: labels.searchPlaceholderSoftware,
          value: this.state.filter.searchValue,
          default: defaults.searchValue,
        },
      ],
    };

    if (this.props.fair?.Halls) {
      filter.dropdowns = [
        {
          cbKey: 'halls',
          label: labels.hall,
          choices: ['all', ...this.props.fair.Halls],
          value: this.state.filter.halls,
          default: defaults.halls,
        },
      ];
      filter.toggles = [
        {
          cbKey: 'show',
          choices: [
            {
              value: 'list',
              label: 'List',
              icon: 'btn-icon-list',
            },
            {
              value: 'map',
              label: 'Map',
              icon: 'btn-icon-map',
            },
          ],
          value: this.state.filter.show,
          default: defaults.show,
        },
      ];
    }

    const child: IFilterModalChild = {
      type: 'dropdowns',
      idx: 0,
      cbKey: 'halls',
      isVisible: this.state.filter.show === 'map',
    };

    const swFilter = new SoftwareFilter(this.state.filter);
    const filteredSoftwares = swFilter.filter(this.props.softwares);

    return (
      <div className="container view-list">
        <FilterModal filter={filter} callback={this.filterCallback} child={child}>
          <FairMap fair={this.props.fair} locationHall={this.state.filter.halls} />
        </FilterModal>
        <ul className="list-group">
          {filteredSoftwares.map((sw) => (
            <SoftwareListElement
              key={sw.name}
              {...sw}
              in={this.isExpanded(sw)}
              fairTag={this.props.fair?.Tag || ''}
              cb={{ expand: () => this.handleExpand(sw.SoftwareId) }}
            />
          ))}
        </ul>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps): ISoftwareListPropsEx => {
  const fairTag: string = ownProps.match.params.fair;
  return {
    softwares: getSoftwareByFairArray(state, fairTag),
    fair: getFair(state, ownProps.match.params.fair),
    filter: getSoftwareListFilter(state, fairTag),
  };
};

export default connect(mapStateToProps, { setSoftwareListFilter })(SoftwareList);
